import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { X, Download, CheckCircle, AlertCircle } from 'lucide-react';
import ModalPortal from './ModalPortal';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 2rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 780px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const SuccessMessage = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const SuccessIcon = styled.div`
  color: #16a34a;
  margin-top: 0.125rem;
`;

const SuccessText = styled.div`
  color: #374151;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const OptionsSection = styled.div`
  margin-bottom: 1.5rem;
`;

const OptionsTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.75rem 0;
`;

const OptionsDescription = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0 0 1rem 0;
`;

const ThresholdGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const ThresholdOption = styled.button`
  background: ${props => props.selected ? '#f3f4f6' : '#f9fafb'};
  border: 1px solid ${props => props.selected ? '#6b7280' : '#d1d5db'};
  border-radius: 8px;
  padding: 0.75rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #374151;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ThresholdLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const ThresholdDescription = styled.div`
  font-size: 0.75rem;
  opacity: 0.8;
`;

const RecordCount = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 0.25rem;
  color: #374151;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: #3b82f6;
  color: white;
  border: 1px solid #3b82f6;
  
  &:hover:not(:disabled) {
    background: #2563eb;
    border-color: #2563eb;
  }
`;

const SecondaryButton = styled(Button)`
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  
  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;

function ExceptionReportModal({ isOpen, onClose, onGenerateReport, isGenerating = false, previewData = null }) {
  const [selectedThreshold, setSelectedThreshold] = useState(10);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const thresholdOptions = [
    { value: 5, label: '5%', description: 'Fields with ≤5% empty values' },
    { value: 10, label: '10%', description: 'Fields with ≤10% empty values' },
    { value: 25, label: '25%', description: 'Fields with ≤25% empty values' },
    { value: 50, label: '50%', description: 'Fields with ≤50% empty values' }
  ].map(option => ({
    ...option,
    recordCount: previewData?.threshold_counts?.[option.value] || 0
  }));

  const handleGenerate = () => {
    onGenerateReport(selectedThreshold);
  };

  return (
    <ModalPortal isOpen={isOpen}>
      <ModalOverlay>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              <CheckCircle size={32} />
              Core Data Coverage Report
            </ModalTitle>
            <CloseButton onClick={onClose}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

        {previewData?.all_fields_have_coverage ? (
          <SuccessMessage>
            <SuccessIcon>
              <CheckCircle size={20} />
            </SuccessIcon>
            <SuccessText>
              <strong>Good news!</strong> All of your enabled fields have some data from the core system.
              You can still generate coverage analysis reports to see which fields have the best data quality.
            </SuccessText>
          </SuccessMessage>
        ) : (
          <SuccessMessage>
            <SuccessIcon>
              <AlertCircle size={20} />
            </SuccessIcon>
            <SuccessText>
              <strong>Coverage Analysis Available</strong> Some fields have lower coverage percentages.
              Select a threshold below to generate a report of fields that need attention.
            </SuccessText>
          </SuccessMessage>
        )}

        <OptionsSection>
          <OptionsTitle>Generate Coverage Analysis Report</OptionsTitle>
          <OptionsDescription>
            {previewData?.all_fields_have_coverage
              ? 'Would you like a report for fields that do have values, but that have fewer loans covered?'
              : 'Select a threshold to generate a report showing fields with good data coverage (low percentage of empty values). This helps identify which fields have the best data quality.'
            }
            {previewData?.total_records && previewData?.total_active_fields && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#6b7280' }}>
                Total records: {previewData.total_records.toLocaleString()} • Active fields: {previewData.total_active_fields}
              </div>
            )}
          </OptionsDescription>

          <ThresholdGrid>
            {thresholdOptions.map(option => (
              <ThresholdOption
                key={option.value}
                selected={selectedThreshold === option.value}
                onClick={() => setSelectedThreshold(option.value)}
                disabled={isGenerating}
              >
                <ThresholdLabel>{option.label}</ThresholdLabel>
                <ThresholdDescription>{option.description}</ThresholdDescription>
                        <RecordCount count={option.recordCount}>
                          {option.recordCount === 0 ? 'No fields' : `(${option.recordCount} field${option.recordCount === 1 ? '' : 's'})`}
                        </RecordCount>
              </ThresholdOption>
            ))}
          </ThresholdGrid>
        </OptionsSection>

        <ButtonGroup>
          <SecondaryButton onClick={onClose} disabled={isGenerating}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleGenerate} disabled={isGenerating}>
            <Download size={20} />
            {isGenerating ? 'Generating...' : 'Generate Report'}
          </PrimaryButton>
        </ButtonGroup>
        </ModalContent>
      </ModalOverlay>
    </ModalPortal>
  );
}

export default ExceptionReportModal;
